import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  CTAGroup,
  ReorderButtons,
  SelectInput,
} from "../../../components/FormField";

import { MediaImageField } from "../../../components/MediaPickerDialog";
import { emptyButton, moveItem } from "./shared";
import { ICON_OPTIONS } from "../../../lib/iconsData";

export function ProductsSections({ setSectionsData, initialData }) {

  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroBadgeText, setHeroBadgeText] = useState("Products");
  const [heroHeadingLine1, setHeroHeadingLine1] = useState("Powerful Platforms. Seamless Integration.");
  const [heroHeadingEmphasis, setHeroHeadingEmphasis] = useState("Built for Australian Businesses.");
  const [heroDescription, setHeroDescription] = useState("");
  const [heroVideo, setHeroVideo] = useState("");
  const [heroPrimary, setHeroPrimary] = useState(emptyButton());
  const [trustBadges, setTrustBadges] = useState([
    { icon: "Award", title: "SAP Partner", sub: "Certified Reseller" },
    { icon: "Users", title: "Salesforce Partner", sub: "Implementation Expert" },
    { icon: "Shield", title: "15+ Products", sub: "Integrated Suite" },
  ]);

  /* ---------------- PRODUCT CATEGORIES ---------------- */
  const [catsActive, setCatsActive] = useState(true);
  const [catsBadgeText, setCatsBadgeText] = useState("Product Categories");
  const [catsHeading, setCatsHeading] = useState("Enterprise-Grade");
  const [catsHeadingEmphasis, setCatsHeadingEmphasis] = useState("Product Ecosystem");
  const [catsDescription, setCatsDescription] = useState(
    "From ERP foundations to advanced analytics, explore our curated suite of enterprise solutions."
  );
  const [categories, setCategories] = useState([
    {
      id: "", title: "", tagline: "", description: "", image: "", icon: "",
      products: [{ title: "", href: "", description: "" }],
    },
  ]);

  /* ---------------- PLATFORM TRUST ---------------- */
  const [trustActive, setTrustActive] = useState(true);
  const [trustBadgeText, setTrustBadgeText] = useState("Platform Partners");
  const [trustHeading, setTrustHeading] = useState("Certified Partner");
  const [trustHeadingEmphasis, setTrustHeadingEmphasis] = useState("Ecosystem");
  const [platformItems, setPlatformItems] = useState([
    { name: "", description: "" },
  ]);

  /* ---------------- WHY OUR PRODUCTS ---------------- */
  const [whyActive, setWhyActive] = useState(true);
  const [whyBadgeText, setWhyBadgeText] = useState("Why Choose Us");
  const [whyHeading, setWhyHeading] = useState("Seamless Integration");
  const [whyHeadingEmphasis, setWhyHeadingEmphasis] = useState("& Support");
  const [whyCards, setWhyCards] = useState([
    { icon: "", title: "", description: "" },
  ]);

  /* ---------------- FINAL CTA ---------------- */
  const [ctaActive, setCtaActive] = useState(true);
  const [ctaHeading, setCtaHeading] = useState("Find the Right Product for Your Business");
  const [ctaDescription, setCtaDescription] = useState("");
  const [ctaPrimary, setCtaPrimary] = useState(emptyButton());
  const [ctaContact, setCtaContact] = useState(
    "+61 3 7075 3900 | info@thynkprocess.com.au | South Melbourne, Victoria"
  );

  const updateBtn = (setter, key, val) => setter((b) => ({ ...b, [key]: val }));

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;

    if (d.hero) {
      setHeroBadgeText(d.hero.badge_text || "");
      setHeroHeadingLine1(d.hero.heading_line1 || "");
      setHeroHeadingEmphasis(d.hero.heading_emphasis || "");
      setHeroDescription(d.hero.description || "");
      setHeroVideo(d.hero.video || "");
      if (d.hero.primary_cta)
        setHeroPrimary({ text: d.hero.primary_cta.text || "", url: d.hero.primary_cta.link || "", newTab: false });
      if (d.hero.trust_badges?.length) setTrustBadges(d.hero.trust_badges);
    }

    if (d.product_categories) {
      setCatsBadgeText(d.product_categories.badge_text || "");
      setCatsHeading(d.product_categories.heading || "");
      setCatsHeadingEmphasis(d.product_categories.heading_emphasis || "");
      setCatsDescription(d.product_categories.description || "");
      if (d.product_categories.categories?.length)
        setCategories(d.product_categories.categories);
    }

    if (d.platform_trust) {
      setTrustBadgeText(d.platform_trust.badge_text || "");
      setTrustHeading(d.platform_trust.heading || "");
      setTrustHeadingEmphasis(d.platform_trust.heading_emphasis || "");
      if (d.platform_trust.items?.length) setPlatformItems(d.platform_trust.items);
    }

    if (d.why_our_products) {
      setWhyBadgeText(d.why_our_products.badge_text || "");
      setWhyHeading(d.why_our_products.heading || "");
      setWhyHeadingEmphasis(d.why_our_products.heading_emphasis || "");
      if (d.why_our_products.cards?.length) setWhyCards(d.why_our_products.cards);
    }

    if (d.final_cta) {
      setCtaHeading(d.final_cta.heading || "");
      setCtaDescription(d.final_cta.description || "");
      setCtaContact(d.final_cta.contact || "");
      if (d.final_cta.primary_cta)
        setCtaPrimary({ text: d.final_cta.primary_cta.text || "", url: d.final_cta.primary_cta.link || "", newTab: false });
    }
  }, [initialData]);

  /* ---------------- BUILD PAYLOAD ---------------- */
  const buildPayload = () => ({
    hero: {
      badge_text: heroBadgeText,
      heading_line1: heroHeadingLine1,
      heading_emphasis: heroHeadingEmphasis,
      description: heroDescription,
      video: heroVideo,
      primary_cta: { text: heroPrimary.text, link: heroPrimary.url },
      trust_badges: trustBadges,
    },
    product_categories: {
      badge_text: catsBadgeText,
      heading: catsHeading,
      heading_emphasis: catsHeadingEmphasis,
      description: catsDescription,
      categories,
    },
    platform_trust: {
      badge_text: trustBadgeText,
      heading: trustHeading,
      heading_emphasis: trustHeadingEmphasis,
      items: platformItems,
    },
    why_our_products: {
      badge_text: whyBadgeText,
      heading: whyHeading,
      heading_emphasis: whyHeadingEmphasis,
      cards: whyCards,
    },
    final_cta: {
      heading: ctaHeading,
      description: ctaDescription,
      contact: ctaContact,
      primary_cta: { text: ctaPrimary.text, link: ctaPrimary.url },
    },
  });

  useEffect(() => {
    setSectionsData?.(buildPayload());
  }, [
    heroBadgeText, heroHeadingLine1, heroHeadingEmphasis, heroDescription,
    heroVideo, heroPrimary, trustBadges,
    catsBadgeText, catsHeading, catsHeadingEmphasis, catsDescription, categories,
    trustBadgeText, trustHeading, trustHeadingEmphasis, platformItems,
    whyBadgeText, whyHeading, whyHeadingEmphasis, whyCards,
    ctaHeading, ctaDescription, ctaContact, ctaPrimary,
  ]);

  /* ---------------- RENDER ---------------- */
  return (
    <>
      {/* HERO */}
      <CollapsibleSection title="Hero Section" active={heroActive} onToggleActive={setHeroActive}>
        <Field label="Badge Text">
          <TextInput value={heroBadgeText} onChange={(e) => setHeroBadgeText(e.target.value)} />
        </Field>
        <Field label="Heading Line 1">
          <TextInput value={heroHeadingLine1} onChange={(e) => setHeroHeadingLine1(e.target.value)} />
        </Field>
        <Field label="Heading Emphasis (gradient line)">
          <TextInput value={heroHeadingEmphasis} onChange={(e) => setHeroHeadingEmphasis(e.target.value)} />
        </Field>
        <Field label="Description">
          <TextArea value={heroDescription} onChange={(e) => setHeroDescription(e.target.value)} />
        </Field>
        <Field label="Hero Video">
          <MediaImageField label="Select Hero Video" value={heroVideo} onChange={setHeroVideo} />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={heroPrimary.text} url={heroPrimary.url} newTab={heroPrimary.newTab}
          onTextChange={(v) => updateBtn(setHeroPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroPrimary, "newTab", v)}
        />

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Trust Badges</p>
          <button
            onClick={() => setTrustBadges((s) => [...s, { icon: "", title: "", sub: "" }])}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Badge
          </button>
        </div>
        {trustBadges.map((b, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Badge {i + 1}</p>
              {trustBadges.length > 1 && (
                <button onClick={() => setTrustBadges((s) => s.filter((_, idx) => idx !== i))}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <Field label="Icon">
              <SelectInput
                value={b.icon} options={ICON_OPTIONS}
                onChange={(e) => setTrustBadges((s) => s.map((x, idx) => idx === i ? { ...x, icon: e.target.value } : x))}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Title">
                <TextInput value={b.title}
                  onChange={(e) => setTrustBadges((s) => s.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))} />
              </Field>
              <Field label="Sub">
                <TextInput value={b.sub}
                  onChange={(e) => setTrustBadges((s) => s.map((x, idx) => idx === i ? { ...x, sub: e.target.value } : x))} />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* PRODUCT CATEGORIES */}
      <CollapsibleSection title="Product Categories" active={catsActive} onToggleActive={setCatsActive}>
        <Field label="Badge Text">
          <TextInput value={catsBadgeText} onChange={(e) => setCatsBadgeText(e.target.value)} />
        </Field>
        <Field label="Heading">
          <TextInput value={catsHeading} onChange={(e) => setCatsHeading(e.target.value)} />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput value={catsHeadingEmphasis} onChange={(e) => setCatsHeadingEmphasis(e.target.value)} />
        </Field>
        <Field label="Description">
          <TextArea value={catsDescription} onChange={(e) => setCatsDescription(e.target.value)} />
        </Field>

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Categories</p>
          <button
            onClick={() => setCategories((s) => [...s, { id: "", title: "", tagline: "", description: "", image: "", icon: "", products: [{ title: "", href: "", description: "" }] }])}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Category
          </button>
        </div>
        {categories.map((cat, ci) => (
          <div key={ci} className="border p-3 rounded-lg mb-4 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Category {ci + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons index={ci} total={categories.length} onMove={(f, t) => setCategories(moveItem(categories, f, t))} />
                {categories.length > 1 && (
                  <button onClick={() => setCategories((s) => s.filter((_, idx) => idx !== ci))}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="ID (slug, e.g. sap-b1)">
              <TextInput value={cat.id}
                onChange={(e) => setCategories((s) => s.map((x, idx) => idx === ci ? { ...x, id: e.target.value } : x))} />
            </Field>
            <Field label="Title">
              <TextInput value={cat.title}
                onChange={(e) => setCategories((s) => s.map((x, idx) => idx === ci ? { ...x, title: e.target.value } : x))} />
            </Field>
            <Field label="Tagline">
              <TextInput value={cat.tagline}
                onChange={(e) => setCategories((s) => s.map((x, idx) => idx === ci ? { ...x, tagline: e.target.value } : x))} />
            </Field>
            <Field label="Description">
              <TextArea value={cat.description}
                onChange={(e) => setCategories((s) => s.map((x, idx) => idx === ci ? { ...x, description: e.target.value } : x))} />
            </Field>
            <Field label="Image">
              <MediaImageField label="Select Category Image" value={cat.image}
                onChange={(v) => setCategories((s) => s.map((x, idx) => idx === ci ? { ...x, image: v } : x))} />
            </Field>
            <Field label="Icon">
              <SelectInput value={cat.icon} options={ICON_OPTIONS}
                onChange={(e) => setCategories((s) => s.map((x, idx) => idx === ci ? { ...x, icon: e.target.value } : x))} />
            </Field>

            {/* Products within category */}
            <div className="flex justify-between mt-4 mb-2">
              <p className="text-xs font-semibold">Products</p>
              <button
                onClick={() => setCategories((s) => s.map((x, idx) => idx === ci ? { ...x, products: [...x.products, { title: "", href: "", description: "" }] } : x))}
                className="flex items-center gap-1 text-xs"
              >
                <Plus size={14} /> Add Product
              </button>
            </div>
            {cat.products.map((prod, pi) => (
              <div key={pi} className="border border-dashed p-3 rounded-lg mb-2 space-y-2 ml-2">
                <div className="flex justify-between">
                  <p className="text-xs font-semibold text-muted-foreground">Product {pi + 1}</p>
                  <div className="flex gap-2">
                    <ReorderButtons index={pi} total={cat.products.length}
                      onMove={(f, t) => setCategories((s) => s.map((x, idx) => idx === ci ? { ...x, products: moveItem(x.products, f, t) } : x))} />
                    {cat.products.length > 1 && (
                      <button onClick={() => setCategories((s) => s.map((x, idx) => idx === ci ? { ...x, products: x.products.filter((_, pidx) => pidx !== pi) } : x))}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <Field label="Title">
                  <TextInput value={prod.title}
                    onChange={(e) => setCategories((s) => s.map((x, idx) => idx === ci ? { ...x, products: x.products.map((p, pidx) => pidx === pi ? { ...p, title: e.target.value } : p) } : x))} />
                </Field>
                <Field label="Link (href)">
                  <TextInput value={prod.href}
                    onChange={(e) => setCategories((s) => s.map((x, idx) => idx === ci ? { ...x, products: x.products.map((p, pidx) => pidx === pi ? { ...p, href: e.target.value } : p) } : x))} />
                </Field>
                <Field label="Description">
                  <TextArea value={prod.description}
                    onChange={(e) => setCategories((s) => s.map((x, idx) => idx === ci ? { ...x, products: x.products.map((p, pidx) => pidx === pi ? { ...p, description: e.target.value } : p) } : x))} />
                </Field>
              </div>
            ))}
          </div>
        ))}
      </CollapsibleSection>

      {/* PLATFORM TRUST */}
      <CollapsibleSection title="Platform Trust" active={trustActive} onToggleActive={setTrustActive}>
        <Field label="Badge Text">
          <TextInput value={trustBadgeText} onChange={(e) => setTrustBadgeText(e.target.value)} />
        </Field>
        <Field label="Heading">
          <TextInput value={trustHeading} onChange={(e) => setTrustHeading(e.target.value)} />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput value={trustHeadingEmphasis} onChange={(e) => setTrustHeadingEmphasis(e.target.value)} />
        </Field>

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Partners</p>
          <button
            onClick={() => setPlatformItems((s) => [...s, { name: "", description: "" }])}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Partner
          </button>
        </div>
        {platformItems.map((p, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Partner {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons index={i} total={platformItems.length} onMove={(f, t) => setPlatformItems(moveItem(platformItems, f, t))} />
                {platformItems.length > 1 && (
                  <button onClick={() => setPlatformItems((s) => s.filter((_, idx) => idx !== i))}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name">
                <TextInput value={p.name}
                  onChange={(e) => setPlatformItems((s) => s.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} />
              </Field>
              <Field label="Description">
                <TextInput value={p.description}
                  onChange={(e) => setPlatformItems((s) => s.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* WHY OUR PRODUCTS */}
      <CollapsibleSection title="Why Our Products" active={whyActive} onToggleActive={setWhyActive}>
        <Field label="Badge Text">
          <TextInput value={whyBadgeText} onChange={(e) => setWhyBadgeText(e.target.value)} />
        </Field>
        <Field label="Heading">
          <TextInput value={whyHeading} onChange={(e) => setWhyHeading(e.target.value)} />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput value={whyHeadingEmphasis} onChange={(e) => setWhyHeadingEmphasis(e.target.value)} />
        </Field>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Cards</p>
          <button
            onClick={() => setWhyCards((s) => [...s, { icon: "", title: "", description: "" }])}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {whyCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Card {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons index={i} total={whyCards.length} onMove={(f, t) => setWhyCards(moveItem(whyCards, f, t))} />
                {whyCards.length > 1 && (
                  <button onClick={() => setWhyCards((s) => s.filter((_, idx) => idx !== i))}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Icon">
              <SelectInput value={c.icon} options={ICON_OPTIONS}
                onChange={(e) => setWhyCards((s) => s.map((x, idx) => idx === i ? { ...x, icon: e.target.value } : x))} />
            </Field>
            <Field label="Title">
              <TextInput value={c.title}
                onChange={(e) => setWhyCards((s) => s.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))} />
            </Field>
            <Field label="Description">
              <TextArea value={c.description}
                onChange={(e) => setWhyCards((s) => s.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* FINAL CTA */}
      <CollapsibleSection title="Final CTA" active={ctaActive} onToggleActive={setCtaActive}>
        <Field label="Heading">
          <TextInput value={ctaHeading} onChange={(e) => setCtaHeading(e.target.value)} />
        </Field>
        <Field label="Description">
          <TextArea value={ctaDescription} onChange={(e) => setCtaDescription(e.target.value)} />
        </Field>
        <Field label="Contact Line">
          <TextInput value={ctaContact} onChange={(e) => setCtaContact(e.target.value)} />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={ctaPrimary.text} url={ctaPrimary.url} newTab={ctaPrimary.newTab}
          onTextChange={(v) => updateBtn(setCtaPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaPrimary, "newTab", v)}
        />
      </CollapsibleSection>
    </>
  );
}